import React from 'react';
import { TBar, ABar } from './Widgets';
import { FormControl, Select, MenuItem, InputLabel, Box, IconButton, SelectChangeEvent } from '@mui/material';
import { formatTime, sortKeys, LeaderboardAggregate, LeaderboardSpecific, SeriesDescr, RaceEntry, LeaderboardEntry,
    RaceSeries, getRunner, formatDateString, serverRequest, serverRequestBlob, ISNATIVE } from './Util';
import { MeetingFinishersPopup } from './Meetings';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import '../Running.css';
import { GlobalContext } from '../context/GlobalContextProvider';

enum AwardType {
    XC_AWARD = 0,
    SIMPLE_AWARD = 1,
    SINGLE_AWARD = 2,
    CHAMPS_AWARD = 3
}

interface SeriesProps {
    selSerie: number;
    awards: number;
    year: number;
    onEsc: (event: React.MouseEvent<HTMLElement>) => void;
}

interface SeriesState {
    series: RaceSeries;
    param: string;
    selRaceID: number;
    selRunnerID: string;
}

export class Series extends React.Component<SeriesProps, SeriesState> {
    static contextType = GlobalContext;

    constructor(props: SeriesProps) {
        super(props);
        var defParam = this.props.awards === AwardType.XC_AWARD ? "M" : "ALL";
        this.state = { series: {} as RaceSeries, param: defParam, selRaceID: 0, selRunnerID: '' }
        this.handleParam = this.handleParam.bind(this);
        this.onSelectRace = this.onSelectRace.bind(this);
        this.onPrint = this.onPrint.bind(this);
    }

    handleParam(e: SelectChangeEvent) {
        this.setState({param: e.target.value}, () => { this.loadSeries(); });
    }

    closeRace = () => {
        this.setState({ selRaceID: 0, selRunnerID: '' });
    }

    onSelectRace = (e: React.MouseEvent<HTMLElement>) => {
        const raceKey = e.currentTarget.dataset.key;
        var ID = raceKey ? Number(raceKey) : 0;
        if (isNaN(ID))
            ID = 0;
        this.setState( { selRaceID: ID === this.state.selRaceID ? 0 : ID } );
    }
    
    componentDidMount() {
        this.loadSeries();
    }

    onPrint() {
        serverRequestBlob("printseries?id=" + this.props.selSerie + "&param=" + this.state.param)
        .then(blob => {
            var url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const fname = "Champs " + this.props.selSerie + " " + this.state.param + ".pdf";
            link.href = url;
            link.setAttribute('download', fname);
            link.click();
        });
    }

    loadSeries() {
        // TBD: escape param or 'MV60+' becomes 'MV60 '
        serverRequest("series?id=" + this.props.selSerie + "&param=" + this.state.param)
        .then(res => {
            this.setState( {series: res} );
        });
    }

    renderFilter(award: number) {
        var style = {
            color: 'var(--primary-color)',
            fontSize: '1.4rem',
            }

        // Award:
        // XC: Male/Female 
        // SIMPLE: ALL/Male/Female
        // SINGLE RACE: ALL - Single ranking, awarding overall AG Res, top 3 male/female, top age groups male/female, top AG Res male/female (for single competition champs)
        // CHAMPS: ALL/Invididual Age Groups (MSEN, WSEN, MV40, WV40, etc. we award 1st, 2nd, 3rd for all categories)
        var gender_filter;
        switch(award) {
            case AwardType.XC_AWARD:
                gender_filter = <FormControl className='noprint filter-form-control' margin='dense' sx={{width: '20ch'}}>
                                    <InputLabel sx={style}>Leaderboard</InputLabel>
                                    <Select value={this.state.param} label="Gender" onChange={this.handleParam} sx={style}>
                                        <MenuItem key='W' value='W'>Women</MenuItem>
                                        <MenuItem key='M' value='M'>Men</MenuItem>
                                    </Select>
                            </FormControl>
                break;
            case AwardType.SIMPLE_AWARD:
                gender_filter = <FormControl className='noprint filter-form-control' margin='dense' sx={{width: '20ch'}}>
                                    <InputLabel sx={style}>Leaderboard</InputLabel>
                                    <Select value={this.state.param} label="Gender" onChange={this.handleParam} sx={style}>
                                        <MenuItem key='ALL' value='ALL'>Age Graded</MenuItem>
                                        <MenuItem key='W' value='W'>Women</MenuItem>
                                        <MenuItem key='M' value='M'>Men</MenuItem>
                                    </Select>
                            </FormControl>
                break;
            case AwardType.SINGLE_AWARD:
                gender_filter = <></>
                break;
            case AwardType.CHAMPS_AWARD:
                gender_filter = <FormControl className='noprint filter-form-control' margin='dense' sx={{width: '20ch'}}>
                                    <InputLabel sx={style}>Leaderboard</InputLabel>
                                    <Select value={this.state.param} label="Gender" onChange={this.handleParam} sx={style}>
                                        <MenuItem key='ALL' value='ALL'>Age Graded</MenuItem>
                                        <MenuItem key='WSEN' value='WSEN'>Women SEN</MenuItem>
                                        <MenuItem key='MSEN' value='MSEN'>Men SEN</MenuItem>
                                        <MenuItem key='WV40' value='WV40'>Women V40</MenuItem>
                                        <MenuItem key='MV40' value='MV40'>Men V40</MenuItem>
                                        <MenuItem key='WV50' value='WV50'>Women V50</MenuItem>
                                        <MenuItem key='MV50' value='MV50'>Men V50</MenuItem>
                                        <MenuItem key='WV60' value='WV60'>Women V60</MenuItem>
                                        <MenuItem key='MV60' value='MV60'>Men V60</MenuItem>
                                        <MenuItem key='WV70+' value='WV70+'>Women V70+</MenuItem>
                                        <MenuItem key='MV70+' value='MV70+'>Men V70+</MenuItem>
                                    </Select>
                            </FormControl>
                break;
        }

        return gender_filter;
    }

    renderRaceTable(racekeys: string[], races: Record<string, RaceEntry>, award: AwardType) {
        var rows;

        // Single generic event: do not include the race table at all
        if (racekeys.length === 1) {
            var race = races[racekeys[0]]
            const [, , , , venue, ] = race
            if (venue === '-')
                return <></>
        }

        rows = racekeys.map((key, raceind) => {
            var race = races[key]
            if (race.length === 6) {
                const [, event, date, meeting, venue, priority] = race
                const generic = venue === '-';
                const racenum = racekeys.length > 1 ? <th className={priority ? 'champs-priority' : ''}>{"R" + (raceind+1)}</th> : <></>;
                const pr_badge = award === AwardType.XC_AWARD ? (
                                    <td><span className={priority ? 'champs-priority-badge champs-priority-yes' : 'champs-priority-badge champs-priority-no'}>
                                        {priority ? 'Priority' : 'Standard'}
                                    </span></td>
                                    ) : <></>;

                if (generic) {
                    return (<tr>
                            {racenum}
                            <td></td>
                            <td>{event}</td>
                            <td></td>
                            <td></td>
                            {pr_badge}
                        </tr>);   
                } else {
                    return (<tr>
                            {racenum}
                            <td className="champs-race-date">{formatDateString(date)}</td>
                            <td>{event}</td>
                            <td className="champs-race-meeting">{meeting}</td>
                            <td className="champs-race-meeting">{venue}</td>
                            {pr_badge}
                        </tr>);   
                }
            }       
            return null;
        });
        return (
                    <div className="champsLeaderboardWrap">                
                        <table><tbody>{rows}</tbody></table>
                    </div>
        );
    }

    renderLeaderboard(leaderboard: Record<string, LeaderboardEntry>, award: number, racekeys: string[], races: Record<string, RaceEntry>) {
        var head;
        if (award !== AwardType.SINGLE_AWARD) {
            var cells = racekeys.map((key, raceind) => {
                var race = races[key]
                const [, , , , , priority] = race
                const cls = priority ? 'champs-priority' : '';
                return (
                    <th colSpan={2} className={cls}>
                        {"R" + (raceind+1)}
                    </th>
                );            
            });
            head = <tr><th className='champs-header-sticky'>Name</th><th>Pos</th><th>AG</th><th>Score</th><th>Avg</th>{cells}<th>Reason</th></tr>;
        } else
            head = <tr><th className='champs-header-sticky'>Name</th><th>Pos</th><th>AG</th><th>Time</th><th>AG Res</th></tr>;

        var leadkeys = Object.keys(leaderboard)

        if (award !== AwardType.SINGLE_AWARD) {
            // Sort by aggregate points and qualified flag
            // If both runners qualify or both don't qualify, compare with score, otherwise the qualified wins anyway
            leadkeys.sort(function compareFn(a, b) {
                const a_aggr: LeaderboardAggregate = leaderboard[a][0];
                const b_aggr: LeaderboardAggregate = leaderboard[b][0];
                if (a_aggr[3] === b_aggr[3])
                    return (a_aggr[1] < b_aggr[1]) ? 1 : -1;
                return a_aggr[3] < b_aggr[3] ? 1 : -1;
            });
        } else {
            // Sort by points only
            leadkeys.sort(function compareFn(a, b) {
                const a_aggr: LeaderboardAggregate = leaderboard[a][0];
                const b_aggr: LeaderboardAggregate = leaderboard[b][0];
                return (a_aggr[1] < b_aggr[1]) ? 1 : -1;
            });
        }

        var classes;
        var rows = leadkeys.map((key, leadind) => {
            const aggreg: LeaderboardAggregate = leaderboard[key][0];
            const [rID, points, scores, quali, AG, award_str, reason, ] = aggreg;

            const specific_list: LeaderboardSpecific[] = leaderboard[key][1];

            const runner = getRunner(rID);
            var fincells: JSX.Element[] = [];

            for (var n=0; n<specific_list.length; n++) {
                var race: LeaderboardSpecific = specific_list[n];
                const [valid, time, AGRes, score, meetID, , , ] = race;
                const create_link = !isNaN(Number(meetID));

                classes = (quali && valid) ? '' : 'disabledcell';
                if (award !== AwardType.SINGLE_AWARD) {
                    if (score > 0) {
                        // TBD: we should access the actual event for proper time formatting
                        var perf = this.state.param === "ALL" ? AGRes.toFixed(1) + "%" : formatTime(time, '5K');
                        if (create_link)
                            fincells.push(<td className={'link champs-perf ' + classes} onClick={this.onSelectRace} data-key={meetID}>{perf}</td>);
                        else
                            fincells.push(<td className={'champs-perf ' + classes}>{perf}</td>);
                        fincells.push(<td className={'borderright ' + classes}><b>{score}</b></td>);
                    } else {
                        fincells.push(<td colSpan={2} className='borderright'>--</td>);
                    }
                } else {
                    if (create_link) {
                        fincells.push(<td className={'link champs-score ' + classes} onClick={this.onSelectRace} data-key={meetID}>{formatTime(time, '5K')}</td>);
                        fincells.push(<td className={'link champs-perf ' + classes} onClick={this.onSelectRace} data-key={meetID}>{AGRes.toFixed(1) + "%"}</td>);
                    } else {
                        fincells.push(<td className={classes}>{formatTime(time, '5K')}</td>);
                        fincells.push(<td className={'champs-perf ' + classes}>{AGRes.toFixed(1) + "%"}</td>);
                    }
                }
            }

            classes = quali ? '' : 'disabledcell';
            var scorestyle = {} // {fontSize: '1.6rem'}

            if (award !== AwardType.SINGLE_AWARD) {
                var avg = scores >= 1 ?  (points / scores).toFixed(1) : '';
                return <tr>
                            <td className='champs-sticky'>{runner.First + " " + runner.Last}</td>
                            <td>{award_str}</td>
                            <td>{AG}</td>
                            <td className={'champs-score ' + classes} style={scorestyle}><b>{points}</b></td>
                            <td className={'borderright ' + classes} >{avg}</td>
                            {fincells}
                            <td>{reason}</td>
                            </tr>
            } else {
                return <tr>
                            <td className='champs-sticky'>{runner.First + " " + runner.Last}</td>
                            <td>{award_str}</td>
                            <td>{runner.Gender + " " + AG}</td>
                            {fincells}
                            </tr>
            }
        });

        var table = (
            <div className="champsLeaderboardWrap">
                <table className="bicolortable champsLeaderboardTable">
                <thead>{head}</thead>
                <tbody>{rows}</tbody>
                </table>
            </div>
            )
        return (<div className="paper champs-leaderboard-card">{table}</div>);
    }

    renderRaceOverlay() {
        return (
            <MeetingFinishersPopup
                raceID={this.state.selRaceID}
                runnerID=""
                deepLinkBase="/races"
                onClose={this.closeRace}
            />
        );
    }

    render() {
        if (Object.keys(this.state.series).length < 1) {
            return <></>
        }

        var leaderboard = this.state.series.leaderboard;
        var races = this.state.series.races;
        var award = parseInt(this.state.series.agres);
        if (award === undefined) award = AwardType.SINGLE_AWARD;

        const print_button = !ISNATIVE ? <IconButton onClick={this.onPrint}>
                                    <FileDownloadOutlinedIcon fontSize='large'/>
                                </IconButton>
                            : <></>

        const gender_filter = this.renderFilter(award);
        var racekeys = Object.keys(races);
        racekeys.sort(function compareFn(a, b) { return (races[a][0] < races[b][0]) ? -1 : 1; });

        const racetable = this.renderRaceTable(racekeys, races, award);
        const table = this.renderLeaderboard(leaderboard, award, racekeys, races);
        const raceOverlay = this.renderRaceOverlay();

        var title;
        if (award === AwardType.SINGLE_AWARD) {
            title = this.props.year + " | " + this.state.series.name;
        } else {
            var racestr = this.state.series.missing > 1 ? " races" : " race";
            var missing = this.state.series.missing > 0 ? " - " + this.state.series.missing + racestr + " to go" : '';
            title = this.state.series.name + " - " + (this.state.param === 'M' ? 'Men' : (this.state.param === 'W' ? 'Women' : 'Age Graded')) + missing;
        }

        return (
            <div className="champs-page">
                <div className="champs-toolbar">
                    {gender_filter}
                    {print_button}
                    <span className="champs-title">{title}</span>
                </div>

                {!!racekeys.length && (
                    <div className="paper champs-races-card">
                        {racetable}
                    </div>
                )}

                {table}
                {raceOverlay}
            </div>
        )
    }
}

interface SeriesSelectorProps {}
interface SeriesSelectorState {
    list: {};
    selSerie: number;
    awards: number;
    year: number;
}

export class SeriesSelector extends React.Component<SeriesSelectorProps, SeriesSelectorState> {
    static contextType = GlobalContext;

    constructor(props: SeriesSelectorProps) {
        super(props);
        this.state = { list: {}, selSerie: 0, awards: AwardType.XC_AWARD, year: 0 }
        this.onSelectSerie = this.onSelectSerie.bind(this);
        this.onEsc = this.onEsc.bind(this);
    }

    onSelectSerie(e: React.MouseEvent<HTMLElement>) {
        const key = e.currentTarget.dataset.key;
        const awardsKey = e.currentTarget.dataset.awardsKey;
        const yearKey = e.currentTarget.dataset.yearKey;
        if (!key || !awardsKey) return;
        this.setState({ selSerie: Number(key), awards: Number(awardsKey), year: Number(yearKey) })
    }

    onEsc(e: React.MouseEvent<HTMLElement>) {
        this.setState({ selSerie: 0});
    }

    componentDidMount() {
        this.loadSeries();
    }

    loadSeries() {
        serverRequest("series")
        .then(res => {
            this.setState( {list: res} );
        });
    }

    openMenu = () => {
        const ctx = this.context as React.ContextType<typeof GlobalContext>;
        ctx.setMenuOpen(!ctx.menuOpen);
    }

    getSeriesList(keys: string[], list: Record<string, SeriesDescr>) {
        var rows = keys.map((key, ind) => {
            var serie: SeriesDescr = list[key];
            const awards = Number(serie.Awards);

            const rowClass =
                awards === AwardType.CHAMPS_AWARD ? 'champ-row champ-annual' :
                    awards === AwardType.XC_AWARD ? 'champ-row champ-xc' : 'champ-row champ-minor';

            return (
                <tr
                    key={serie.ID}
                    onClick={this.onSelectSerie}
                    data-key={serie.ID}
                    data-awards-key={awards}
                    data-year-key={serie.Year}
                    className={rowClass}
                >
                    <td className="yearcol">{serie.Year}</td>
                    <td className="descrcol compcol">
                        <div className="comp-name">{serie.Name}</div>
                        <div className="comp-descr">{serie.Descr}</div>
                    </td>
                </tr>
            );
        });
        return rows;
    }

    render() {
        var page = <div></div>
        var leftmenu = <></>
        if (this.state.selSerie) {
            page = <Series selSerie={this.state.selSerie} awards={this.state.awards} year={this.state.year} onEsc={this.onEsc} />
            leftmenu = <IconButton onClick={this.onEsc} style={{display: 'inline', marginRight: '10px', color: 'var(--light-text-color)'}}>
                            <ArrowBackIosIcon />
                    </IconButton>
        } else {
            var list = this.state.list;
            var keys = sortKeys(this.state.list, [{key: 'ID', asc: false}]);
            var rows = this.getSeriesList(keys, list)
            var head = <tr><th>Year</th><th>Competition</th></tr>
            page = (
            <div className="limitcontainer champsTableWrap">
                <table className="fixedtable champsTable">
                <colgroup>
                    <col className="col-year" />
                    <col className="col-comp" />
                </colgroup>

                <thead>{head}</thead>
                <tbody>{rows}</tbody>
                </table>
            </div>
            );
        }

        var appbar = <ABar position="top" >
                    <TBar>
                        {leftmenu}
                        <IconButton sx={{ color: 'var(--paper-color)'}} onClick={this.openMenu}>
                            <MenuIcon fontSize="large"></MenuIcon>
                        </IconButton>
                        <div className='largetext' >Club Championships</div>
                        <Box sx={{ flexGrow: 1}} />
                    </TBar>
                    </ABar>

        return (
          <div>
            {appbar}
            <div style={{padding: '16px'}}>{page}</div>
          </div>)
    }
}
